/**
 * パスワード / パスフレーズ生成サービス (T-D-Tools)。
 *
 * 設計判断:
 *   - Math.random() の代わりに crypto.getRandomValues を使用 (CSPRNG)。
 *   - 偏りを排除するため rejection sampling (limit = floor(2^32 / max) * max)。
 *   - PASSPHRASE_WORDS は 256 語の簡易ワードリスト。
 *     Phase 4: EFF Long List (7776 語) に置き換えて entropy を 64+ bit に強化予定。
 */

const cryptoRandIndex = (max: number): number => {
  const arr = new Uint32Array(1)
  const limit = Math.floor(0x100000000 / max) * max
  let v: number
  do {
    crypto.getRandomValues(arr)
    v = arr[0]!
  } while (v >= limit)

  return v % max
}

export const POOL_UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
export const POOL_LOWER = 'abcdefghijklmnopqrstuvwxyz'
export const POOL_NUM = '0123456789'
export const POOL_SYM = '!@#$%^&*()_+-=[]{}|;:,.<>?'

export interface PasswordOptions {
  length: number
  useUpper: boolean
  useLower: boolean
  useNum: boolean
  useSym: boolean
}

export const generatePassword = (opts: PasswordOptions): string => {
  let pool = ''
  if (opts.useUpper) pool += POOL_UPPER
  if (opts.useLower) pool += POOL_LOWER
  if (opts.useNum) pool += POOL_NUM
  if (opts.useSym) pool += POOL_SYM
  if (!pool) return ''

  return Array.from({ length: opts.length }, () => pool[cryptoRandIndex(pool.length)]).join('')
}

export const calcPoolSize = (opts: Omit<PasswordOptions, 'length'>): number => {
  let n = 0
  if (opts.useUpper) n += POOL_UPPER.length
  if (opts.useLower) n += POOL_LOWER.length
  if (opts.useNum) n += POOL_NUM.length
  if (opts.useSym) n += POOL_SYM.length

  return n
}

export const calcEntropyBits = (opts: PasswordOptions): number => {
  const ps = calcPoolSize(opts)
  if (ps === 0) return 0

  return Math.round(opts.length * Math.log2(ps))
}

/** entropy に基づく強度スコア (1–4)。strength CSS クラスの s1–s4 に対応。 */
export const entropyToScore = (bits: number): 1 | 2 | 3 | 4 => {
  if (bits < 40) return 1
  if (bits < 60) return 2
  if (bits < 80) return 3

  return 4
}

/** entropy に基づく強度ラベルの i18n key。 */
export const entropyToLabelKey = (bits: number): string => {
  if (bits < 40) return 'tools.generator.strength.weak'
  if (bits < 60) return 'tools.generator.strength.fair'
  if (bits < 80) return 'tools.generator.strength.strong'

  return 'tools.generator.strength.very_strong'
}

export interface PassphraseOptions {
  wordCount: number
  separator: string
  includeNumber: boolean
  capitalize: boolean
}

export const generatePassphrase = (opts: PassphraseOptions): string => {
  const picked = Array.from({ length: opts.wordCount }, () => {
    const w = PASSPHRASE_WORDS[cryptoRandIndex(PASSPHRASE_WORDS.length)]!

    return opts.capitalize ? w.charAt(0).toUpperCase() + w.slice(1) : w
  })

  if (opts.includeNumber) {
    picked.push(String(cryptoRandIndex(100)))
  }

  return picked.join(opts.separator)
}

export const calcPassphraseEntropy = (opts: PassphraseOptions): number => {
  let bits = opts.wordCount * Math.log2(PASSPHRASE_WORDS.length)
  if (opts.includeNumber) bits += Math.log2(100)

  return Math.round(bits)
}

// ── ユーザー名生成 (#3) ─────────────────────────────────────────

/** ユーザー名生成モード:
 *   - word    : ランダムワード連結 (例: cleverotter)
 *   - random  : 小文字 + 数字のランダム英数字列 (例: k3p9xq2a)
 *   - email   : ランダム local-part + ユーザー指定 domain (例: k3p9xq2a@gmail.com) */
export type UsernameMode = 'word' | 'random' | 'email'

export interface UsernameOptions {
  mode: UsernameMode
  /** word モード: 連結する単語数。 */
  wordCount: number
  /** word モード: 各単語の先頭を大文字化する。 */
  capitalize: boolean
  /** word モード: 末尾に 2 桁の数字を付ける。 */
  appendNumber: boolean
  /** random / email モード: 生成する文字列 (email モードでは local-part) 長。 */
  length: number
  /** email モード: `@` 以降のドメイン。先頭 `@` 有無は受理し、内部で剥がす。
   *  空欄なら `example.com` を仮置きする (UI 側で placeholder 提示)。 */
  emailDomain: string
}

/** random モードの文字プール (ユーザー名向け: 小文字 + 数字)。 */
const USERNAME_POOL = `${POOL_LOWER}${POOL_NUM}`

/**
 * ユーザー名を生成する。
 *   - `word`: PASSPHRASE_WORDS から wordCount 語を連結 (区切り無し)。capitalize /
 *     末尾 2 桁数字は任意。例: `cleverotter` / `CleverOtter42`
 *   - `random`: 小文字 + 数字のランダム列。例: `k3p9xq2a`
 */
export const generateUsername = (opts: UsernameOptions): string => {
  if (opts.mode === 'random' || opts.mode === 'email') {
    const len = Math.max(3, Math.min(64, opts.length))

    const local = Array.from(
      { length: len },
      () => USERNAME_POOL[cryptoRandIndex(USERNAME_POOL.length)],
    ).join('')

    if (opts.mode === 'random') return local

    // email: local-part + @ + ユーザー指定 domain。先頭 @ は剥がす、空欄は仮置き。
    const domain = sanitizeEmailDomain(opts.emailDomain)

    return `${local}@${domain}`
  }

  const count = Math.max(1, Math.min(5, opts.wordCount))

  const words = Array.from({ length: count }, () => {
    const w = PASSPHRASE_WORDS[cryptoRandIndex(PASSPHRASE_WORDS.length)]!

    return opts.capitalize ? w.charAt(0).toUpperCase() + w.slice(1) : w
  })

  // 末尾 2 桁数字 (10〜99) — 衝突回避と「数字必須」サイト対策。
  return words.join('') + (opts.appendNumber ? String(cryptoRandIndex(90) + 10) : '')
}

/** email domain を表示用に正規化:
 *  - 先頭 `@` を剥がす (UI 上 `@gmail.com` と書きがち)
 *  - 前後の空白を除去
 *  - 空文字 / 無効値は `example.com` を仮置き (検証は呼出側で行う想定) */
export const sanitizeEmailDomain = (raw: string): string => {
  const trimmed = (raw ?? '').trim().replace(/^@/, '')

  return trimmed === '' ? 'example.com' : trimmed
}

/**
 * パスフレーズ用ワードリスト (256 語)。
 * Phase 4: EFF Long List (7776 語) に置き換え予定。
 */
export const PASSPHRASE_WORDS: readonly string[] = [
  'able',
  'acid',
  'aged',
  'also',
  'arch',
  'area',
  'army',
  'atom',
  'baby',
  'back',
  'ball',
  'band',
  'base',
  'bath',
  'bear',
  'bell',
  'belt',
  'bird',
  'blow',
  'blue',
  'boat',
  'body',
  'bold',
  'bond',
  'bone',
  'book',
  'boot',
  'bowl',
  'bulk',
  'burn',
  'calm',
  'card',
  'care',
  'case',
  'cave',
  'city',
  'clay',
  'clip',
  'club',
  'coal',
  'coat',
  'code',
  'cold',
  'comb',
  'cook',
  'cool',
  'copy',
  'core',
  'corn',
  'cost',
  'crab',
  'crew',
  'crop',
  'cube',
  'curl',
  'dark',
  'data',
  'dawn',
  'dead',
  'deal',
  'deck',
  'deer',
  'deep',
  'desk',
  'dice',
  'diet',
  'dirt',
  'disk',
  'dock',
  'door',
  'draw',
  'drum',
  'duck',
  'dune',
  'dust',
  'earn',
  'east',
  'edge',
  'epic',
  'exam',
  'face',
  'fact',
  'fair',
  'fall',
  'farm',
  'fast',
  'fate',
  'felt',
  'file',
  'fill',
  'film',
  'find',
  'fire',
  'fish',
  'flag',
  'flat',
  'flip',
  'flow',
  'foam',
  'fold',
  'folk',
  'fond',
  'food',
  'fork',
  'form',
  'fort',
  'free',
  'fuel',
  'full',
  'fund',
  'fuse',
  'game',
  'gate',
  'gear',
  'gift',
  'glad',
  'glow',
  'glue',
  'gold',
  'good',
  'grab',
  'gray',
  'grid',
  'grin',
  'grip',
  'grow',
  'gulf',
  'gust',
  'hack',
  'hail',
  'hair',
  'hall',
  'halt',
  'hand',
  'hang',
  'hard',
  'hawk',
  'head',
  'heap',
  'heat',
  'heel',
  'help',
  'herb',
  'hike',
  'hill',
  'hint',
  'hole',
  'home',
  'hook',
  'hope',
  'horn',
  'host',
  'hour',
  'hull',
  'hunt',
  'idea',
  'iron',
  'isle',
  'item',
  'jack',
  'jade',
  'join',
  'jump',
  'just',
  'keen',
  'keep',
  'kind',
  'king',
  'knee',
  'knit',
  'knot',
  'know',
  'lack',
  'lake',
  'lamp',
  'land',
  'lark',
  'lava',
  'lawn',
  'lead',
  'leaf',
  'lean',
  'left',
  'lens',
  'lick',
  'life',
  'lift',
  'lime',
  'link',
  'list',
  'live',
  'load',
  'lock',
  'loft',
  'long',
  'loop',
  'loss',
  'lure',
  'mast',
  'math',
  'maze',
  'meal',
  'meat',
  'memo',
  'mesh',
  'mild',
  'mind',
  'mint',
  'mode',
  'moon',
  'most',
  'move',
  'musk',
  'nail',
  'navy',
  'need',
  'nest',
  'news',
  'next',
  'nice',
  'nick',
  'noon',
  'note',
  'oath',
  'only',
  'open',
  'oven',
  'over',
  'pace',
  'pack',
  'pact',
  'page',
  'palm',
  'park',
  'past',
  'path',
  'peak',
  'pear',
  'peer',
  'pile',
  'pine',
  'pink',
  'pipe',
  'plan',
  'play',
  'plot',
  'plow',
  'plum',
] as const
